import { Controller } from "@hotwired/stimulus"

// Renderiza barras de progresso reais durante Active Storage direct upload.
// Escuta eventos `direct-upload:*` que bubbliam do <input type="file"> dentro
// do escopo do controller.
//
// Markup:
//   <div data-controller="upload-progress">
//     <input type="file" data-direct-upload-url="..." />
//     <div data-upload-progress-target="strip"></div>
//   </div>
//
// Seguranca:
//   - Filenames sao inseridos via `textContent`, NAO innerHTML (anti-XSS).
//   - Sem inline scripts (compativel com CSP nonce).
//   - Server-side e a unica autoridade pra limites; este controller e UX puro.
//   - event.detail.id (uuid Active Storage) usado so como chave interna no DOM,
//     nunca exfiltrado.

export default class extends Controller {
  static targets = ["strip"]
  static classes = ["item", "bar", "fill", "label", "percent"]

  connect() {
    this._items = new Map()
    this._handlers = {
      initialize: this._onInitialize.bind(this),
      progress: this._onProgress.bind(this),
      error: this._onError.bind(this),
      end: this._onEnd.bind(this)
    }
    this.element.addEventListener("direct-upload:initialize", this._handlers.initialize)
    this.element.addEventListener("direct-upload:progress", this._handlers.progress)
    this.element.addEventListener("direct-upload:error", this._handlers.error)
    this.element.addEventListener("direct-upload:end", this._handlers.end)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:initialize", this._handlers.initialize)
    this.element.removeEventListener("direct-upload:progress", this._handlers.progress)
    this.element.removeEventListener("direct-upload:error", this._handlers.error)
    this.element.removeEventListener("direct-upload:end", this._handlers.end)
    this._items.clear()
  }

  _onInitialize(event) {
    const { id, file } = event.detail
    if (!this.hasStripTarget) return

    // Stimulus `static classes` accessors (this.itemClass, ...) THROW
    // "Missing attribute data-...-class" when the view does not supply
    // the optional class attribute — the `||` fallback never runs. Guard
    // each one with has*Class so the controller degrades to its default
    // BEM classes when a view (e.g. the simplified #382 template form)
    // mounts it without declaring the classes.
    const item = document.createElement("div")
    item.className = this.hasItemClass ? this.itemClass : "upload-progress__item"
    item.dataset.uploadId = id

    const label = document.createElement("span")
    label.className = this.hasLabelClass ? this.labelClass : "upload-progress__label"
    label.textContent = file.name  // textContent escapa, anti-XSS

    const bar = document.createElement("div")
    bar.className = this.hasBarClass ? this.barClass : "upload-progress__bar"

    const fill = document.createElement("div")
    fill.className = this.hasFillClass ? this.fillClass : "upload-progress__fill"
    fill.style.width = "0%"

    const percent = document.createElement("span")
    percent.className = this.hasPercentClass ? this.percentClass : "upload-progress__percent"
    percent.textContent = "0%"

    bar.appendChild(fill)
    item.appendChild(label)
    item.appendChild(bar)
    item.appendChild(percent)
    this.stripTarget.appendChild(item)

    this._items.set(id, { item, fill, percent, label })
  }

  _onProgress(event) {
    const { id, progress } = event.detail
    const entry = this._items.get(id)
    if (!entry) return

    const pct = Math.max(0, Math.min(100, Math.round(progress)))
    entry.fill.style.width = pct + "%"
    entry.percent.textContent = pct + "%"
  }

  _onError(event) {
    const { id } = event.detail
    // Active Storage chama event.preventDefault() pra suprimir o alert padrao;
    // a gente ja esta mostrando o erro inline.
    event.preventDefault()
    const entry = this._items.get(id)
    if (!entry) return

    entry.item.classList.add("upload-progress__item--error")
    entry.percent.textContent = "✗"
    // Mensagem de erro do AS pode ser tecnica ("Error storing..."); resumimos.
    entry.label.textContent = entry.label.textContent + " — falhou"
  }

  _onEnd(event) {
    const { id } = event.detail
    const entry = this._items.get(id)
    if (!entry) return

    // Erro ja marcou o item; nao remove em caso de erro pra usuario ver.
    if (entry.item.classList.contains("upload-progress__item--error")) return

    // Fade out + remove em 400ms pra UX limpa.
    entry.item.classList.add("upload-progress__item--done")
    setTimeout(() => {
      entry.item.remove()
      this._items.delete(id)
    }, 400)
  }
}
